// src/components/admin/EmailCampaignsEditor.jsx - Part 1 of 2
// WITH CUSTOMER PREVIEW MODAL AND ENHANCED TARGETING

import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  limit
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { getCustomerTargetingPreview, getSalonServices, getSalonStaff } from '../../api/salonApi';
import { applyIndustryContent, getSalonIndustry } from '../../utils/industryContent';
import { findUnresolvedPlaceholders, personalizeMessage, getSalonPhone, getSalonName, getCustomerAppointment, getCustomerTransaction } from '../../utils/campaignPersonalization';
import '../../styles/customer-preview-modal.css';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import { applyFrequencyExclusionToList, recordCampaignDeliveries } from '../../api/campaignExclusionService';
import { useSalonLocations } from '../../hooks/useSalonLocations';

// Customer Preview Modal Component
function CustomerPreviewModal({ isOpen, onClose, previewData, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const customersPerPage = 100;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (!isOpen || !previewData) return null;

  const { eligibleCustomers = [], eligibleCount = 0, totalCount = 0 } = previewData;

  const filteredCustomers = eligibleCustomers.filter(customer => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      customer.displayName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email'];
    const csvRows = [headers.join(',')];

    sortedCustomers.forEach(customer => {
      const row = [
        `"${customer.displayName || ''}"`,
        `"${customer.email || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `email-campaign-recipients-${campaignName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${date}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>Email Recipients Preview</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Eligible Recipients</span>
            <span className="summary-value">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>

        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <button 
                className="export-csv-btn" 
                onClick={handleExportCSV}
                disabled={sortedCustomers.length === 0}
              >
                📥 Export to CSV
              </button>

              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button 
                    className="filter-clear-btn"
                    onClick={() => setFilterText('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              {currentCustomers.length > 0 ? (
                <>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th 
                            onClick={() => handleSort('displayName')}
                            className={sortField === 'displayName' ? `sorted-${sortDirection}` : ''}
                          >
                            Customer Name
                            <span className="sort-indicator"></span>
                          </th>
                          <th 
                            onClick={() => handleSort('email')}
                            className={sortField === 'email' ? `sorted-${sortDirection}` : ''}
                          >
                            Email Address
                            <span className="sort-indicator"></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.map((customer, index) => (
                          <tr key={customer.id || index}>
                            <td>{customer.displayName || 'N/A'}</td>
                            <td>{customer.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <p className="empty-state-text">No customers match your search</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">No eligible recipients found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailCampaignsEditor({ salonId, onUpdate, prefillHtml, prefillTextBody, prefillSubject, onPrefillConsumed, prefillCampaign, onCampaignPrefillConsumed }) {
  const { currentUser } = useSalonAuth();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(''); // '', 'test', 'actual', or 'template'
  const [success, setSuccess] = useState('');
  
  const [emailErrors, setEmailErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [showImageInput, setShowImageInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // NEW: State for tracking if targeting criteria has been saved
  const [targetingCriteriaSaved, setTargetingCriteriaSaved] = useState(false);
  
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('create-campaign');

  // Template state
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('transactional');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customTemplates, setCustomTemplates] = useState({ transactional: [], promotional: [] });
  const [campaignSettings, setCampaignSettings] = useState({});
  const [templateEditMode, setTemplateEditMode] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // State for appointment-based targeting
  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);
  const [loadingServicesStaff, setLoadingServicesStaff] = useState(false);
  const [salonPhone, setSalonPhone] = useState('');
  const [industry, setIndustry] = useState('hair');
  useEffect(() => {
    if (!salonId) return;
    getSalonIndustry(salonId).then(setIndustry);
  }, [salonId]);
  const [salonName, setSalonName] = useState('');
  const [salonConfig, setSalonConfig] = useState(null);
  // HTML template mode (set when AI generates a full HTML email)
  const [isHtmlTemplate, setIsHtmlTemplate] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile');

  // UPDATED: Default values with "not_selected" options
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    schedulingType: 'one-time', // one-time or recurring
    scheduledDate: '',
    scheduledTime: '',
    conversionPeriod: 30, // days after send to attribute a booking as a conversion
    recurring: {
      frequency: 'daily', // daily, weekly, monthly, annually
      dayOfWeek: '', // for weekly
      dayOfMonth: 0, // for monthly (1-31)
      monthOfYear: '', // for annually
      dayOfYear: 0, // for annually (1-31)
      startDate: '',
      endDate: '',
      endType: 'date', // date or cycles
      numberOfCycles: 0,
      excludePreviousRecipients: false,
      exclusionType: 'cycles', // cycles or date
      excludePastCycles: 0,
      excludeBeforeDate: ''
    },
    targetingType: 'not_selected',
    targetingCriteria: {
      birthMonths: [],
      postalCodes: [],
      lastTransactionMonths: 0,
      lastTransactionUnit: 'months',
      transactionCount: 0,
      transactionType: 'not_selected',
      transactionAmountType: 'not_selected',
      transactionAmount: 0,
      transactionLocationId: 'not_selected',
      memberTiers: [],
      pointsType: 'not_selected',
      pointsAmount: 0,
      specificCustomers: [],
      notesSearchText: '', // #Revised UI_v2 — customer notes targeting
      notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
      // Appointment-based targeting fields
      appointmentTimeValue: 0,
      appointmentTimeUnit: 'days',
      appointmentService: 'not_selected',
      appointmentStaff: 'not_selected',
      appointmentStatus: []
    }
  });

  // Appointment status options
  const appointmentStatusOptions = ['approved', 'pending', 'rejected', 'cancelled'];

  const birthMonthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const memberTierOptions = [
    'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'
  ];

  const commonEmojis = ['😊', '🎉', '💄', '✨', '💅', '💗', '🌟', '💖', '🔥', '💯', '👑', '💃'];

  // Template id -> field under salons/{salonId}.emailTemplates (read by senders / Cloud Functions)
  const TRANSACTIONAL_FIRESTORE_KEYS = {
    appointment_confirmation: 'appointment_confirmation',
    appointment_rejected: 'appointment_rejected',
    payment_receipt: 'payment_receipt',
    loyalty_points_earned: 'loyalty_points_earned',
    magic_link_signin: 'magicLinkSignIn',
    welcome_email: 'welcomeEmail'
  };

  // Email Campaign Templates
  const emailTemplates = {
    transactional: [
      {
        id: 'appointment_confirmation',
        title: 'Appointment Confirmed - See You Soon!',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        isAutoTriggered: true,
        subject: 'Appointment Confirmed - See You Soon! 📅',
        body: `Hi {name}!

Your appointment has been confirmed!

📅 Date: {date}
⏰ Time: {time}
💇‍♀️ Service: {service}
👤 Staff: {staff}

We look forward to pampering you! If you need to reschedule, please give us a call.

See you soon!`,
        html: `<p>Hi {name}!</p>
<p><strong>Your appointment has been confirmed!</strong></p>
<p>📅 <strong>Date:</strong> {date}<br>
⏰ <strong>Time:</strong> {time}<br>
💇‍♀️ <strong>Service:</strong> {service}<br>
👤 <strong>Staff:</strong> {staff}</p>
<p>We look forward to pampering you! If you need to reschedule, please give us a call.</p>
<p>See you soon!</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'appointment_rejected',
        title: 'Appointment Rejected - Kindly Book Again',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        isAutoTriggered: true,
        subject: 'Appointment Update - Kindly Book Again',
        body: `Hi {name}!

Thank you for your booking request. Unfortunately, we are unable to accommodate your appointment at the requested time.

📅 Date: {date}
⏰ Time: {time}
💇‍♀️ Service: {service}
👤 Staff: {staff}

We sincerely apologise for the inconvenience. Please visit our booking page to select another available date and time that works for you.

We look forward to welcoming you soon!`,
        html: `<p>Hi {name}!</p>
<p>Thank you for your booking request. Unfortunately, we are unable to accommodate your appointment at the requested time.</p>
<p>📅 <strong>Date:</strong> {date}<br>
⏰ <strong>Time:</strong> {time}<br>
💇‍♀️ <strong>Service:</strong> {service}<br>
👤 <strong>Staff:</strong> {staff}</p>
<p>We sincerely apologise for the inconvenience. Please visit our booking page to select another available date and time that works for you.</p>
<p>We look forward to welcoming you soon!</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'payment_receipt',
        title: 'Payment Receipt - Thank You!',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        isAutoTriggered: true,
        subject: 'Payment Receipt - Thank You! 💳',
        body: `Hi {name}!

Thank you for your payment!

💳 Amount Paid: {amount}
📧 Invoice: #{invoice_number}

We appreciate your business and look forward to seeing you at your next appointment!

Thank you for choosing us!`,
        html: `<p>Hi {name}!</p>
<p><strong>Thank you for your payment!</strong></p>
<p>💳 <strong>Amount Paid:</strong> {amount}<br>
📧 <strong>Invoice:</strong> #{invoice_number}</p>
<p>We appreciate your business and look forward to seeing you at your next appointment!</p>
<p>Thank you for choosing us!</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'loyalty_points_earned',
        title: 'You\'ve Earned Loyalty Points!',
        messageCategory: 'transactional',
        messageType: 'loyalty_update',
        isAutoTriggered: true,
        subject: 'You\'ve Earned Loyalty Points! ⭐',
        body: `Hi {name}!

Great news! You've earned loyalty points!

⭐ Points Earned: {points_earned}
💎 Total Balance: {total_points} points

Keep collecting points and enjoy exclusive rewards! Every visit brings you closer to something special.

Thank you for being a valued customer!`,
        html: `<p>Hi {name}!</p>
<p><strong>Great news! You've earned loyalty points!</strong></p>
<p>⭐ <strong>Points Earned:</strong> {points_earned}<br>
💎 <strong>Total Balance:</strong> {total_points} points</p>
<p>Keep collecting points and enjoy exclusive rewards! Every visit brings you closer to something special.</p>
<p>Thank you for being a valued customer!</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'magic_link_signin',
        title: 'Magic Link Sign-In Email',
        messageCategory: 'transactional',
        messageType: 'authentication',
        isSystemTemplate: true,
        systemNote: 'Sent automatically when a customer requests a sign-in link via email',
        subject: 'Sign in to {salonName}',
        body: `Hi there!

Click the button below to sign in to your {salonName} account:

{sign_in_link}

This link will expire in 1 hour. If you didn't request this, you can safely ignore this email.

See you soon!`,
        html: `<p>Hi there!</p>
<p>Click the button below to sign in to your <strong>{salonName}</strong> account:</p>
<p><a href="{sign_in_link}" style="display:inline-block;padding:12px 24px;background-color:#c2185b;color:white;text-decoration:none;border-radius:6px;">Sign In</a></p>
<p>This link will expire in 1 hour. If you didn't request this, you can safely ignore this email.</p>
<p>See you soon!</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'welcome_email',
        title: 'Welcome Email (First-Time Customer)',
        messageCategory: 'transactional',
        messageType: 'welcome',
        isSystemTemplate: true,
        systemNote: 'Sent automatically when a customer signs in for the first time (any method)',
        subject: 'Welcome to {salonName}! We\'re excited to have you',
        body: `Hi {name}!

Welcome to {salonName}! We're thrilled to have you join us.

We can't wait to pamper you. Book your first appointment today!

Warm regards,
The {salonName} Team`,
        html: `<p>Hi <strong>{name}</strong>!</p>
<p>Welcome to <strong>{salonName}</strong>! We're thrilled to have you join us.</p>
<p>We can't wait to pamper you. Book your first appointment today!</p>
<p>Warm regards,<br>The {salonName} Team</p>`,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      }
    ],
    promotional: [
      {
        id: 'monthly_special',
        title: 'This Month\'s Special: 20% Off All Services!',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'This Month\'s Special: 20% Off All Services! 💄',
        body: `Hi there!

🎉 EXCLUSIVE OFFER: 20% OFF ALL SERVICES! 🎉

This month only, treat yourself to any of our premium services and save 20%!

✨ Hair Styling
💅 Manicures & Pedicures
💇‍♀️ Hair Coloring
💆‍♀️ Spa Treatments
...and more!

Don't miss out on this limited-time offer! Book your appointment today.

Valid through the end of the month. Cannot be combined with other offers.`,
        html: `<p>Hi there!</p>
<h2 style="color: #b6a593;">🎉 EXCLUSIVE OFFER: 20% OFF ALL SERVICES! 🎉</h2>
<p>This month only, treat yourself to any of our premium services and save 20%!</p>
<ul>
<li>✨ Hair Styling</li>
<li>💅 Manicures & Pedicures</li>
<li>💇‍♀️ Hair Coloring</li>
<li>💆‍♀️ Spa Treatments</li>
<li>...and more!</li>
</ul>
<p><strong>Don't miss out on this limited-time offer! Book your appointment today.</strong></p>
<p><em>Valid through the end of the month. Cannot be combined with other offers.</em></p>`,
        targetingType: 'customer_points',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 6,
          lastTransactionUnit: 'months',
          transactionCount: 2,
          transactionType: 'greater_than',
          transactionAmountType: 'greater_than',
          transactionAmount: 100,
          memberTiers: ['Gold', 'Platinum'],
          pointsType: 'greater_than',
          pointsAmount: 500,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'birthday_celebration',
        title: 'Happy Birthday! Your Special Gift Awaits',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Happy Birthday! Your Special Gift Awaits 🎂',
        body: `Happy Birthday {name}! 🎂🎉

It's your special month, and we want to celebrate with you!

🎁 YOUR BIRTHDAY GIFT: 25% OFF ANY SERVICE! 🎁

Choose from any of our services and enjoy a special birthday discount throughout your birthday month:

💇‍♀️ Premium Hair Services
💅 Luxury Nail Care
💆‍♀️ Relaxing Spa Treatments
...and so much more!

Treat yourself - you deserve it!

Book your birthday appointment today and let us pamper you!

Valid throughout your birthday month.`,
        html: `<p><strong>Happy Birthday {name}! 🎂🎉</strong></p>
<p>It's your special month, and we want to celebrate with you!</p>
<h2 style="color: #b6a593;">🎁 YOUR BIRTHDAY GIFT: 25% OFF ANY SERVICE! 🎁</h2>
<p>Choose from any of our services and enjoy a special birthday discount throughout your birthday month:</p>
<ul>
<li>💇‍♀️ Premium Hair Services</li>
<li>💅 Luxury Nail Care</li>
<li>💆‍♀️ Relaxing Spa Treatments</li>
<li>...and so much more!</li>
</ul>
<p><strong>Treat yourself - you deserve it!</strong></p>
<p>Book your birthday appointment today and let us pamper you!</p>
<p><em>Valid throughout your birthday month.</em></p>`,
        targetingType: 'customer_profile',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'referral_rewards',
        title: 'Share the Beauty - Refer & Earn!',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Share the Beauty - Refer & Earn! 👯‍♀️',
        body: `Hi {name}!

Love our services? Share the love and get rewarded!

👯‍♀️ OUR REFERRAL PROGRAM 👯‍♀️

Refer a friend and you BOTH get:
💝 $20 OFF your next visit
⭐ Bonus loyalty points
🎁 Exclusive perks

The more friends you refer, the more you save!

How it works:
1. Share your unique referral code: {referral_code}
2. Your friend books their first appointment
3. You both receive your rewards automatically!

Start referring today and enjoy the benefits!`,
        html: `<p>Hi {name}!</p>
<p>Love our services? Share the love and get rewarded!</p>
<h2 style="color: #b6a593;">👯‍♀️ OUR REFERRAL PROGRAM 👯‍♀️</h2>
<p><strong>Refer a friend and you BOTH get:</strong></p>
<ul>
<li>💝 $20 OFF your next visit</li>
<li>⭐ Bonus loyalty points</li>
<li>🎁 Exclusive perks</li>
</ul>
<p><strong>The more friends you refer, the more you save!</strong></p>
<p><strong>How it works:</strong></p>
<ol>
<li>Share your unique referral code: <strong>{referral_code}</strong></li>
<li>Your friend books their first appointment</li>
<li>You both receive your rewards automatically!</li>
</ol>
<p>Start referring today and enjoy the benefits!</p>`,
        targetingType: 'customer_points',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 3,
          lastTransactionUnit: 'months',
          transactionCount: 3,
          transactionType: 'greater_than',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: ['Gold', 'Platinum', 'Diamond'],
          pointsType: 'greater_than',
          pointsAmount: 300,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      }
    ]
  };

  // Load custom templates from localStorage + system template overrides from Firestore
  useEffect(() => {
    let loadedTemplates = { transactional: [], promotional: [] };

    // 1. Load from localStorage
    const savedTemplates = localStorage.getItem(`email_templates_${salonId}`);
    if (savedTemplates) {
      try {
        loadedTemplates = JSON.parse(savedTemplates);
      } catch (err) {
        console.error('Error loading templates from localStorage:', err);
      }
    }

    // 2. Load system template overrides from Firestore
    const loadSystemTemplates = async () => {
      if (!salonId) return;
      try {
        const { doc, getDoc, updateDoc } = await import('firebase/firestore');
        const salonDoc = await getDoc(doc(db, 'salons', salonId));
        if (salonDoc.exists()) {
          const salonData = salonDoc.data();
          setSalonConfig(salonData);
          // Load transactional campaign enabled/disabled settings
          if (salonData.transactionalCampaignSettings) {
            setCampaignSettings(salonData.transactionalCampaignSettings);
          }
          const emailTemplatesData = salonData.emailTemplates || {};

          // Firestore is the source of truth for every auto-triggered template (all admins, the AI agent and the
          // Cloud Functions read it). Firestore key -> template id.
          const merged = { ...loadedTemplates, transactional: [...(loadedTemplates.transactional || [])] };
          const toMigrate = [];
          for (const [templateId, firestoreKey] of Object.entries(TRANSACTIONAL_FIRESTORE_KEYS)) {
            const defaultTemplate = emailTemplates.transactional.find(t => t.id === templateId);
            if (!defaultTemplate) continue;
            const stored = emailTemplatesData[firestoreKey];
            const localIdx = merged.transactional.findIndex(t => t.id === templateId);
            if (stored) {
              const override = {
                ...defaultTemplate,
                subject: stored.subject,
                body: stored.body,
                html: stored.html || convertTextToHtml(stored.body),
                isCustom: true
              };
              if (localIdx !== -1) merged.transactional[localIdx] = override;
              else merged.transactional.push(override);
            } else if (localIdx !== -1 && defaultTemplate.isAutoTriggered) {
              // One-time migration: an edit saved only in this browser is pushed to Firestore so sends use it
              toMigrate.push(merged.transactional[localIdx]);
            }
          }
          for (const t of toMigrate) {
            try {
              await updateDoc(doc(db, 'salons', salonId), {
                [`emailTemplates.${TRANSACTIONAL_FIRESTORE_KEYS[t.id]}`]: {
                  subject: t.subject, body: t.body, html: t.html || convertTextToHtml(t.body)
                }
              });
            } catch (migrateErr) {
              console.error('Could not migrate local template to Firestore:', t.id, migrateErr);
            }
          }
          setCustomTemplates(merged);
          return;
        }
      } catch (err) {
        console.error('Error loading system templates from Firestore:', err);
      }

      // If no Firestore overrides, just use localStorage data
      setCustomTemplates(loadedTemplates);
    };

    loadSystemTemplates();
  }, [salonId]);

  // Load services, staff, and salon phone for appointment-based targeting and personalization
  useEffect(() => {
    const loadServicesStaffAndPhone = async () => {
      if (!salonId) return;

      setLoadingServicesStaff(true);
      try {
        const [services, staff, phone, name] = await Promise.all([
          getSalonServices(salonId),
          getSalonStaff(salonId),
          getSalonPhone(salonId),
          getSalonName(salonId)
        ]);

        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
        setSalonPhone(phone || '');
        setSalonName(name || '');
      } catch (err) {
        console.error('Error loading services, staff, and phone:', err);
      } finally {
        setLoadingServicesStaff(false);
      }
    };

    loadServicesStaffAndPhone();
  }, [salonId]);

  // Apply AI-generated HTML email prefill when it arrives
  useEffect(() => {
    if (!prefillHtml) return;
    setFormData(prev => ({
      ...prev,
      body: prefillHtml,
      subject: prefillSubject || prev.subject
    }));
    setIsHtmlTemplate(true);
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply AI-generated plain text email prefill when it arrives
  useEffect(() => {
    if (!prefillTextBody) return;
    setFormData(prev => ({
      ...prev,
      body: prefillTextBody,
      subject: prefillSubject || prev.subject
    }));
    setIsHtmlTemplate(false);
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillTextBody]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore a failed campaign for editing — user must choose a new send time.
  // Also handles pre-population from Find Prospects (specific_customer targeting).
  useEffect(() => {
    if (!prefillCampaign) return;
    const prefillSpecificCustomers = prefillCampaign.targetingCriteria?.specificCustomers || [];
    const hasSpecificCustomers =
      prefillCampaign.targetingType === 'specific_customer' && prefillSpecificCustomers.length > 0;
    setFormData(prev => ({
      ...prev,
      subject: prefillCampaign.subject || '',
      body: prefillCampaign.body || '',
      scheduledDate: '',
      scheduledTime: '',
      schedulingType: prefillCampaign.schedulingType || 'one-time',
      recurring: prefillCampaign.recurring
        ? { ...prev.recurring, ...prefillCampaign.recurring }
        : prev.recurring,
      targetingType: prefillCampaign.targetingType || 'not_selected',
      // Merge with defaults so every field has a defined value regardless of campaign age
      targetingCriteria: {
        ...prev.targetingCriteria,
        ...(prefillCampaign.targetingCriteria || {}),
      },
    }));
    setIsHtmlTemplate(false);
    // When coming from Find Prospects, pre-populate the customer chips and mark as saved
    if (hasSpecificCustomers) {
      setSelectedCustomers(prefillSpecificCustomers);
      setTargetingCriteriaSaved(true);
    } else {
      setSelectedCustomers([]);
      setTargetingCriteriaSaved(false);
    }
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onCampaignPrefillConsumed) onCampaignPrefillConsumed();
  }, [prefillCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save custom templates to localStorage
  const saveTemplatesToStorage = (templates) => {
    localStorage.setItem(`email_templates_${salonId}`, JSON.stringify(templates));
    setCustomTemplates(templates);
  };

  // Get all templates (default + custom, with deduplication)
  const getAllTemplates = (category) => {
    const customs = customTemplates[category] || [];
    const customIds = new Set(customs.map(t => t.id));
    const defaults = applyIndustryContent('email', emailTemplates[category], industry).filter(t => !customIds.has(t.id));
    return [...defaults, ...customs];
  };

  // Function to load template into form
  const handleLoadTemplate = (template) => {
    setFormData({
      subject: template.subject,
      body: template.body,
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setSelectedTemplate(null);
    setTemplateEditMode(false);
    setTemplateToEdit(null);
    setFieldErrors({});
    setSuccess('Template loaded successfully! Please fill in the scheduling details and customize as needed.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to revise template
  const handleReviseTemplate = (template) => {
    setFormData({
      subject: template.subject,
      body: template.body,
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setTemplateEditMode(true);
    setTemplateToEdit(template);
    setSelectedTemplate(null);
    setFieldErrors({});
    setSuccess('Template loaded for editing. Make your changes and click "Make Revisions to Template" to save.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Toggle transactional campaign enabled/disabled
  const handleToggleCampaign = async (template) => {
    const key = `email_${template.id}`;
    const currentlyEnabled = campaignSettings[key] !== false;
    const newSettings = { ...campaignSettings, [key]: !currentlyEnabled };
    setCampaignSettings(newSettings);

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'salons', salonId), {
        [`transactionalCampaignSettings.${key}`]: !currentlyEnabled
      });
      console.log(`Campaign ${template.id} ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling campaign:', err);
      setCampaignSettings(campaignSettings);
    }
  };

  // Function to save revised template
  const handleSaveRevisedTemplate = async () => {
    if (!templateToEdit) return;

    // Validation
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the subject and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const category = templateToEdit.messageCategory;
    const updatedCustomTemplates = { ...customTemplates };

    if (!updatedCustomTemplates[category]) {
      updatedCustomTemplates[category] = [];
    }

    const revisedTemplate = {
      ...templateToEdit,
      title: templateToEdit.isSystemTemplate ? templateToEdit.title : formData.subject,
      subject: formData.subject,
      body: formData.body,
      html: convertTextToHtml(formData.body),
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    // Find and update existing custom template, or add as new custom override
    const templateIndex = updatedCustomTemplates[category].findIndex(t => t.id === templateToEdit.id);

    if (templateIndex !== -1) {
      updatedCustomTemplates[category][templateIndex] = revisedTemplate;
    } else {
      // Default template being revised for the first time — add as custom override
      updatedCustomTemplates[category].push(revisedTemplate);
    }

    saveTemplatesToStorage(updatedCustomTemplates);

    // Auto-triggered + system templates are saved to Firestore so every sender (any admin's browser, the Bookings
    // AI agent, Cloud Functions) uses the same wording. localStorage above is only a local cache.
    const firestoreKey = TRANSACTIONAL_FIRESTORE_KEYS[templateToEdit.id];
    if (firestoreKey && (templateToEdit.isSystemTemplate || templateToEdit.isAutoTriggered) && salonId) {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, 'salons', salonId), {
          [`emailTemplates.${firestoreKey}`]: {
            subject: formData.subject,
            body: formData.body,
            html: revisedTemplate.html
          }
        });
        console.log(`Template ${templateToEdit.id} saved to Firestore`);
      } catch (firestoreError) {
        console.error('Error saving template to Firestore:', firestoreError);
        setError('Template saved on this device only - could not save it to the salon. Please try again.');
        setTimeout(() => setError(''), 7000);
        return;
      }
    }

    setTemplateEditMode(false);
    setTemplateToEdit(null);
    resetEmailForm();
    setSuccess('Template revised successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  // Function to create new template
  const handleCreateTemplate = () => {
    // Validation
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the subject and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Determine category based on targeting type
    const category = formData.targetingType === 'specific_customer' ? 'transactional' : 'promotional';

    const newTemplate = {
      id: `custom_${Date.now()}`,
      title: formData.subject,
      messageCategory: category,
      messageType: category === 'transactional' ? 'appointment_update' : 'not_selected',
      subject: formData.subject,
      body: formData.body,
      html: convertTextToHtml(formData.body),
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    const updatedCustomTemplates = { ...customTemplates };
    updatedCustomTemplates[category] = [...(updatedCustomTemplates[category] || []), newTemplate];

    saveTemplatesToStorage(updatedCustomTemplates);
    resetEmailForm();
    setSuccess('Template created successfully!');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to templates section
    const templatesElement = document.querySelector('.templates-section');
    if (templatesElement) {
      templatesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to delete template
  const handleDeleteTemplate = () => {
    if (!selectedTemplate || !selectedTemplate.isCustom) {
      setError('You can only delete custom templates');
      setTimeout(() => setError(''), 5000);
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteTemplate = () => {
    const category = selectedTemplate.messageCategory;
    const updatedCustomTemplates = { ...customTemplates };

    updatedCustomTemplates[category] = updatedCustomTemplates[category].filter(
      t => t.id !== selectedTemplate.id
    );

    saveTemplatesToStorage(updatedCustomTemplates);
    setSelectedTemplate(null);
    setShowDeleteConfirm(false);
    setSuccess('Template deleted successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  const buildTargetingCriteria = () => {
    const { targetingType, targetingCriteria } = formData;
    
    const criteria = {};
    
    if (targetingType === 'customer_profile') {
      if (targetingCriteria.birthMonths.length > 0) {
        criteria.birthMonths = targetingCriteria.birthMonths;
      }
      if (targetingCriteria.postalCodes.length > 0) {
        criteria.postalCodes = targetingCriteria.postalCodes;
      }
    } 
    else if (targetingType === 'customer_transaction') {
      criteria.transactionType = targetingCriteria.transactionType;
      criteria.transactionCount = targetingCriteria.transactionCount;
      criteria.lastTransactionMonths = targetingCriteria.lastTransactionMonths;
      criteria.transactionAmountType = targetingCriteria.transactionAmountType;
      criteria.transactionAmount = targetingCriteria.transactionAmount;
      if (targetingCriteria.transactionLocationId && targetingCriteria.transactionLocationId !== 'not_selected') {
        criteria.transactionLocationId = targetingCriteria.transactionLocationId;
      }
    }
    else if (targetingType === 'customer_points') {
      if (targetingCriteria.memberTiers.length > 0) {
        criteria.memberTiers = targetingCriteria.memberTiers;
      }
      criteria.pointsType = targetingCriteria.pointsType;
      criteria.pointsAmount = targetingCriteria.pointsAmount;
    }
    else if (targetingType === 'customer_appointment') {
      criteria.appointmentTimeValue = targetingCriteria.appointmentTimeValue;
      criteria.appointmentTimeUnit = targetingCriteria.appointmentTimeUnit;
      criteria.appointmentService = targetingCriteria.appointmentService;
      criteria.appointmentStaff = targetingCriteria.appointmentStaff;
      criteria.appointmentStatus = targetingCriteria.appointmentStatus;
    }
    else if (targetingType === 'specific_customer') {
      criteria.specificCustomers = targetingCriteria.specificCustomers;
    }

    return criteria;
  };

  // Returns true when the body is a self-contained HTML email (from AI generation)
  const isFullHtmlDocument = (text) => {
    if (!text) return false;
    const t = text.trim();
    return /^<(div|table|section|header|main)\b/i.test(t) && isHtmlTemplate;
  };

  const convertTextToHtml = (text) => {
    if (!text) return '';

    // If content already has structured HTML (paragraphs, lists, headings), return as-is
    if (/<(p|ul|ol|li|h[1-6]|div|blockquote)\b/i.test(text)) return text;

    // Normalize line endings
    const normalized = text.replace(/\r\n/g, '\n');

    // Helper: detect if a line starts with an emoji/symbol character (used as a bullet)
    const isEmojiStart = (line) => {
      const firstChar = [...line.trim()][0];
      if (!firstChar) return false;
      const cp = firstChar.codePointAt(0);
      // U+2600+ covers symbols, dingbats, pictographs, and emoji ranges
      return cp > 0x25FF;
    };

    // Helper: check if a block is a standalone media/void HTML element (e.g. <img>, <hr>)
    const isStandaloneHtmlTag = (block) => {
      return /^<(img|figure|hr|video|iframe|audio)\b[^>]*\/?>$/i.test(block.trim());
    };

    // Process each double-newline-separated block
    const processBlock = (block) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      // Standalone HTML element (e.g. <img src="...">) — keep as-is, centred
      if (isStandaloneHtmlTag(trimmed)) {
        return `<div style="text-align: center; margin: 0 0 16px 0;">${trimmed}</div>`;
      }

      const lines = trimmed.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      if (lines.length === 0) return null;

      // Single-line block → simple paragraph
      if (lines.length === 1) {
        return `<p style="margin: 0 0 16px 0;">${lines[0]}</p>`;
      }

      // Numbered list: every line starts with "N. "
      const isAllNumbered = lines.every(l => /^\d+\.\s/.test(l));
      if (isAllNumbered) {
        const items = lines.map(l => `<li style="margin-bottom: 4px;">${l.replace(/^\d+\.\s+/, '')}</li>`).join('');
        return `<ol style="margin: 0 0 16px 0; padding-left: 24px;">${items}</ol>`;
      }

      // Emoji bullet list: at least 2 lines start with emoji AND majority of lines are emoji-prefixed
      const emojiCount = lines.filter(l => isEmojiStart(l)).length;
      if (emojiCount >= 2 && emojiCount >= Math.ceil(lines.length / 2)) {
        const items = lines.map(l => `<li style="margin-bottom: 4px;">${l}</li>`).join('');
        return `<ul style="margin: 0 0 16px 0; padding-left: 0; list-style: none;">${items}</ul>`;
      }

      // Regular paragraph — convert single newlines to <br>
      return `<p style="margin: 0 0 16px 0;">${lines.join('<br>')}</p>`;
    };

    // Split on double newlines (paragraph breaks) and process each block
    return normalized.split(/\n\n+/).map(processBlock).filter(Boolean).join('');
  };

  const getEmailHtml = (body, messageCategory) => {
    // AI-generated full HTML templates are sent as-is
    if (isFullHtmlDocument(body)) return body;

    const htmlBody = convertTextToHtml(body);
    const termsLink = `${window.location.origin}/BespokeBeauty/${salonId}/admin/terms`;
    const privacyLink = `${window.location.origin}/BespokeBeauty/${salonId}/admin/privacy`;

    const isPromotional = messageCategory === 'promotional';

    const unsubscribeSection = isPromotional ? `
        <p style="font-size: 12px; color: #666; line-height: 1.5;">
          If you no longer wish to receive promotional emails,
          <a href="{unsubUrl}" style="color: #000000;">click here to unsubscribe</a>.
        </p>` : '';

    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        ${htmlBody}
        <br><br><br><br><br>
        <hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;">
        ${unsubscribeSection}
      </div>
    `;
  };

  const handlePreviewTargeting = async () => {
    try {
      setPreviewLoading(true);
      setError('');
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      setPreviewData(preview);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setError('Failed to get targeting preview. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadSocialCSV = async () => {
    try {
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      const customers = preview.eligibleCustomers || [];
      const rows = [['first_name', 'last_name', 'email', 'phone']];
      customers.forEach(c => {
        const nameParts = (c.displayName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const email = (c.email || '').toLowerCase();
        const digitsOnly = (c.phone || '').replace(/\D/g, '');
        const phone = digitsOnly.length === 8 ? `65${digitsOnly}` : digitsOnly;
        rows.push([firstName, lastName, email, phone]);
      });
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'social_platform_audience.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading social CSV:', err);
      setError('Failed to generate CSV. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const searchLower = searchTerm.toLowerCase();

      // Query by email prefix range
      const emailQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('email', '>=', searchLower),
        where('email', '<=', searchLower + ''),
        limit(20)
      );

      // Query by displayName prefix range
      const nameQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + ''),
        limit(20)
      );

      const [emailSnapshot, nameSnapshot] = await Promise.all([
        getDocs(emailQuery),
        getDocs(nameQuery)
      ]);

      const customerMap = new Map();

      const addFromSnapshot = (snapshot) => {
        snapshot.forEach((doc) => {
          if (!customerMap.has(doc.id)) {
            const customerData = doc.data();
            customerMap.set(doc.id, {
              id: doc.id,
              email: customerData.email || null,
              displayName: customerData.displayName || null,
              phoneNumber: customerData.phoneNumber || null
            });
          }
        });
      };

      addFromSnapshot(emailSnapshot);
      addFromSnapshot(nameSnapshot);

      const customers = Array.from(customerMap.values());
      
      const uniqueCustomers = customers.filter((customer, index, self) => 
        index === self.findIndex(c => c.id === customer.id) &&
        !selectedCustomers.some(selected => selected.id === customer.id)
      );
      
      setCustomerSearchResults(uniqueCustomers);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchQuery(value);
    setShowCustomerDropdown(true);
    
    const timeoutId = setTimeout(() => {
      searchCustomers(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleCustomerSelect = (customer) => {
    const updatedCustomers = [...selectedCustomers, customer];
    setSelectedCustomers(updatedCustomers);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const handleCustomerRemove = (customerId) => {
    const updatedCustomers = selectedCustomers.filter(customer => customer.id !== customerId);
    setSelectedCustomers(updatedCustomers);
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const validateField = (fieldName, value) => {
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'subject':
        if (!value || !value.trim()) {
          errorMessage = 'Subject is required';
          isValid = false;
        }
        break;
      case 'body':
        if (!value || !value.trim()) {
          errorMessage = 'Email content is required';
          isValid = false;
        }
        break;
      case 'scheduledDate':
        if (formData.schedulingType === 'one-time' && !value) {
          errorMessage = 'Scheduled date is required';
          isValid = false;
        }
        break;
      case 'scheduledTime':
        if (!value) {
          errorMessage = 'Scheduled time is required';
          isValid = false;
        }
        break;
      case 'targetingType':
        if (value === 'not_selected') {
          errorMessage = 'Target audience is required';
          isValid = false;
        }
        break;
      default:
        break;
    }

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));

    return isValid;
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateEmailCampaign = () => {
    const errors = {};

    if (formData.messageCategory === 'promotional') {
      const leftovers = [...findUnresolvedPlaceholders(formData.subject, { promotional: true }), ...findUnresolvedPlaceholders(formData.body, { promotional: true })];
      if (leftovers.length) errors.body = `Replace these placeholders before sending: ${[...new Set(leftovers)].join(', ')}`;
    }

    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!formData.body.trim()) {
      errors.body = 'Email content is required';
    }

    if (!formData.scheduledTime) {
      errors.scheduledTime = 'Scheduled time is required';
    }

    // One-time campaign validation
    if (formData.schedulingType === 'one-time') {
      if (!formData.scheduledDate) {
        errors.scheduledDate = 'Scheduled date is required';
      }

      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        const now = new Date();
        if (scheduledDateTime <= now) {
          errors.scheduledDateTime = 'Scheduled date and time must be in the future';
        }
      }
    }

    // Recurring campaign validation
    if (formData.schedulingType === 'recurring') {
      if (!formData.recurring.startDate) {
        errors.recurringStartDate = 'Start date is required for recurring campaigns';
      }

      if (formData.recurring.endType === 'date' && !formData.recurring.endDate) {
        errors.recurringEndDate = 'End date is required';
      }

      if (formData.recurring.endType === 'date' && formData.recurring.startDate && formData.recurring.endDate) {
        if (new Date(formData.recurring.endDate) <= new Date(formData.recurring.startDate)) {
          errors.recurringEndDate = 'End date must be after start date';
        }
      }

      if (formData.recurring.endType === 'cycles' && formData.recurring.numberOfCycles < 1) {
        errors.numberOfCycles = 'Number of cycles must be at least 1';
      }

      if (formData.recurring.startDate) {
        const startDate = new Date(formData.recurring.startDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (startDate < now) {
          errors.recurringStartDate = 'Start date must be today or in the future';
        }
      }

      // Exclusion settings validation
      if (formData.recurring.excludePreviousRecipients) {
        if (formData.recurring.exclusionType === 'cycles' && formData.recurring.excludePastCycles < 1) {
          errors.excludePastCycles = 'Number of past cycles must be at least 1';
        }

        if (formData.recurring.exclusionType === 'date' && !formData.recurring.excludeBeforeDate) {
          errors.excludeBeforeDate = 'Exclusion cutoff date is required';
        }

        if (formData.recurring.exclusionType === 'date' && formData.recurring.excludeBeforeDate && formData.recurring.startDate) {
          if (new Date(formData.recurring.excludeBeforeDate) >= new Date(formData.recurring.startDate)) {
            errors.excludeBeforeDate = 'Exclusion cutoff date must be before campaign start date';
          }
        }
      }
    }

    if (formData.targetingType === 'specific_customer' && formData.targetingCriteria.specificCustomers.length === 0) {
      errors.specificCustomers = 'Please select at least one customer for specific customer targeting';
    }

    setEmailErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getAdminUsers = async () => {
    try {
      const adminQuery = query(
        collection(db, 'salonUsers'),
        where('salonId', '==', salonId)
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      const adminUsers = [];
      
      adminSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (['owner', 'admin', 'manager'].includes(userData.role)) {
          adminUsers.push(userData);
        }
      });
      
      console.log(`Found ${adminUsers.length} admin users for salon ${salonId}:`, adminUsers);
      
      return adminUsers;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  };

  const getTargetedCustomers = async () => {
    try {
      console.log('Getting targeted customers for salonId:', salonId);
      
      const customersRef = collection(db, 'customers');
      const customersQuery = query(
        customersRef,
        where('salonId', '==', salonId)
      );
      
      const customersSnapshot = await getDocs(customersQuery);
      let customers = [];
      
      customersSnapshot.forEach((doc) => {
        const customerData = doc.data();
        customers.push({
          id: doc.id,
          ...customerData
        });
      });
      
      console.log(`Found ${customers.length} total customers for salon ${salonId}`);
      
      customers = customers.filter(customer => 
        customer.preferences?.notifications?.email === true
      );
      
      console.log(`${customers.length} customers have email notifications enabled`);
      
      let targetedCustomers = customers;
      
      if (formData.targetingType === 'customer_profile') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.birthMonths.length > 0) {
            if (!formData.targetingCriteria.birthMonths.includes(customer.birthMonth)) {
              return false;
            }
          }
          
          if (formData.targetingCriteria.postalCodes.length > 0) {
            const customerPostal = customer.postalCode || '';
            const matchesPostal = formData.targetingCriteria.postalCodes.some(code => 
              customerPostal.startsWith(code)
            );
            if (!matchesPostal) {
              return false;
            }
          }
          
          return true;
        });
      } 
      else if (formData.targetingType === 'customer_transaction') {
        const txLocId = formData.targetingCriteria.transactionLocationId;
        targetedCustomers = customers.filter(customer => {
          // Location filter: if a specific location is chosen, match by customer's locationId
          if (txLocId && txLocId !== 'not_selected') {
            if (customer.locationId !== txLocId) return false;
          }

          const totalVisits = customer.metrics?.totalVisits || 0;
          const totalSpent = customer.metrics?.totalSpent || 0;
          const lastTransaction = customer.metrics?.lastTransaction;

          const transactionCount = formData.targetingCriteria.transactionCount;
          const transactionType = formData.targetingCriteria.transactionType;

          if (transactionType === 'less_than' && totalVisits >= transactionCount) {
            return false;
          }
          if (transactionType === 'equal_to' && totalVisits !== transactionCount) {
            return false;
          }
          if (transactionType === 'greater_than' && totalVisits <= transactionCount) {
            return false;
          }

          const transactionAmount = formData.targetingCriteria.transactionAmount;
          const transactionAmountType = formData.targetingCriteria.transactionAmountType;

          if (transactionAmountType === 'greater_than' && totalSpent <= transactionAmount) {
            return false;
          }
          if (transactionAmountType === 'less_than' && totalSpent >= transactionAmount) {
            return false;
          }

          if (lastTransaction) {
            const lastTransactionDate = lastTransaction.toDate ? lastTransaction.toDate() : new Date(lastTransaction);
            const timeValue = formData.targetingCriteria.lastTransactionMonths;
            const timeUnit = formData.targetingCriteria.lastTransactionUnit || 'months';
            const cutoffDate = new Date();

            switch (timeUnit) {
              case 'hours':
                cutoffDate.setHours(cutoffDate.getHours() - timeValue);
                break;
              case 'days':
                cutoffDate.setDate(cutoffDate.getDate() - timeValue);
                break;
              case 'months':
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
                break;
              case 'years':
                cutoffDate.setFullYear(cutoffDate.getFullYear() - timeValue);
                break;
              default:
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
            }

            if (lastTransactionDate < cutoffDate) {
              return false;
            }
          }

          return true;
        });
      }
      else if (formData.targetingType === 'customer_points') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.memberTiers.length > 0) {
            if (!formData.targetingCriteria.memberTiers.includes(customer.loyaltyTier)) {
              return false;
            }
          }
          
          const pointsBalance = customer.metrics?.pointsBalance || 0;
          const pointsAmount = formData.targetingCriteria.pointsAmount;
          const pointsType = formData.targetingCriteria.pointsType;
          
          if (pointsType === 'greater_than' && pointsBalance <= pointsAmount) {
            return false;
          }
          if (pointsType === 'less_than' && pointsBalance >= pointsAmount) {
            return false;
          }
          
          return true;
        });
      }
      else if (formData.targetingType === 'customer_appointment') {
        // Query appointments collection based on criteria
        const appointmentsRef = collection(db, 'appointments');
        const appointmentsSnapshot = await getDocs(appointmentsRef);

        // Calculate the cutoff date for upcoming appointments
        const now = new Date();
        const cutoffDate = new Date();
        const timeValue = formData.targetingCriteria.appointmentTimeValue;
        const timeUnit = formData.targetingCriteria.appointmentTimeUnit;

        switch (timeUnit) {
          case 'hours':
            cutoffDate.setHours(cutoffDate.getHours() + timeValue);
            break;
          case 'days':
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
            break;
          case 'months':
            cutoffDate.setMonth(cutoffDate.getMonth() + timeValue);
            break;
          default:
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
        }

        // Filter appointments based on criteria
        const matchingCustomerIds = new Set();

        appointmentsSnapshot.forEach((doc) => {
          const appointment = doc.data();

          // Check if appointment belongs to this salon
          if (appointment.salonId !== salonId) return;

          // Check dateTimeRequested is within the range
          const appointmentDate = appointment.dateTimeRequested?.toDate
            ? appointment.dateTimeRequested.toDate()
            : new Date(appointment.dateTimeRequested);

          if (appointmentDate < now || appointmentDate > cutoffDate) return;

          // Check service filter
          if (formData.targetingCriteria.appointmentService !== 'not_selected') {
            if (appointment.serviceId !== formData.targetingCriteria.appointmentService &&
                appointment.service !== formData.targetingCriteria.appointmentService) return;
          }

          // Check staff filter
          if (formData.targetingCriteria.appointmentStaff !== 'not_selected') {
            if (appointment.staffId !== formData.targetingCriteria.appointmentStaff &&
                appointment.staff !== formData.targetingCriteria.appointmentStaff) return;
          }

          // Check status filter
          if (formData.targetingCriteria.appointmentStatus.length > 0) {
            if (!formData.targetingCriteria.appointmentStatus.includes(appointment.status)) return;
          }

          // Add customer ID to the matching set
          if (appointment.customerId) {
            matchingCustomerIds.add(appointment.customerId);
          }
        });

        // Filter customers based on matching appointment customer IDs
        targetedCustomers = customers.filter(customer =>
          matchingCustomerIds.has(customer.id)
        );
      }
      else if (formData.targetingType === 'specific_customer') {
        const selectedCustomerIds = formData.targetingCriteria.specificCustomers.map(c => c.id);
        targetedCustomers = customers.filter(customer =>
          selectedCustomerIds.includes(customer.id)
        );
      }
      else if (formData.targetingType === 'customer_notes') { // #Revised UI_v2
        const searchText = (formData.targetingCriteria.notesSearchText || '').trim().toLowerCase();
        if (searchText) {
          const matchType = formData.targetingCriteria.notesMatchType || 'contains_all';
          targetedCustomers = customers.filter(customer => {
            const notes = (customer.customer_notes || '').toLowerCase();
            if (!notes) return false;
            if (matchType === 'exact') return notes.includes(searchText);
            const words = searchText.split(/[\s,]+/).filter(w => w.length > 0);
            if (matchType === 'contains_any') return words.some(word => notes.includes(word));
            return words.every(word => notes.includes(word)); // contains_all
          });
        }
      }

      // Filter out customers without email and return full customer objects
      const customersWithEmail = targetedCustomers.filter(customer => customer.email);

      console.log(`Targeted ${customersWithEmail.length} customers after filtering`);

      return customersWithEmail;
    } catch (error) {
      console.error('Error getting targeted customers:', error);
      throw error;
    }
  };

  const showValidationErrors = (errors) => {
    const errorMessages = Object.values(errors).filter(msg => msg);
    if (errorMessages.length > 0) {
      alert(`Please fix the following errors:\n\n• ${errorMessages.join('\n• ')}`);
    }
  };

  // NEW: Handler for saving targeting criteria
  const handleSaveTargetingCriteria = () => {
    setTargetingCriteriaSaved(true);
    setSuccess('Targeting criteria saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

// src/components/admin/EmailCampaignsEditor.jsx - Part 2 of 2
// Event Handlers and JSX Render
// This continues immediately after Part 1

  const handleSendTestEmailBlast = async () => {
    if (!validateEmailCampaign()) {
      showValidationErrors(emailErrors);
      return;
    }

    try {
      setSaving('test');
      setError('');

      const adminUsers = await getAdminUsers();

      if (adminUsers.length === 0) {
        throw new Error('No admin users found for this salon');
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      // Check if message contains personalization placeholders
      const hasPersonalization = formData.subject.includes('{') || formData.body.includes('{');

      // For test emails, create a sample customer object using admin info for demo
      const sampleCustomer = {
        id: 'test-customer',
        displayName: adminUsers[0]?.displayName || adminUsers[0]?.email?.split('@')[0] || 'Test Customer',
        email: adminUsers[0]?.email,
        metrics: {
          pointsBalance: 250,
          totalVisits: 5,
          totalSpent: 500
        }
      };

      // Create sample appointment and transaction for demo
      const sampleAppointment = {
        dateTimeRequested: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        serviceId: salonServices[0]?.id,
        staffId: salonStaff[0]?.id
      };

      const sampleTransaction = {
        totalAmount: 150,
        pointsEarned: 15
      };

      let personalizedSubject = formData.subject;
      let personalizedBody = formData.body;

      if (hasPersonalization) {
        personalizedSubject = await personalizeMessage(formData.subject, sampleCustomer, {
            promotional: formData.messageCategory === 'promotional',
          salonId,
          salonName,
          appointment: sampleAppointment,
          transaction: sampleTransaction,
          salonPhone,
          services: salonServices,
          staffList: salonStaff
        });

        personalizedBody = await personalizeMessage(formData.body, sampleCustomer, {
            promotional: formData.messageCategory === 'promotional',
          salonId,
          salonName,
          appointment: sampleAppointment,
          transaction: sampleTransaction,
          salonPhone,
          services: salonServices,
          staffList: salonStaff
        });
      }

      const campaignData = {
        type: 'email',
        salonId: salonId,
        subject: personalizedSubject,
        html: getEmailHtml(personalizedBody, formData.messageCategory),
        to: adminUsers.map(user => user.email),
        schedulingType: formData.schedulingType,
        recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
        metadata: {
          campaignType: 'test_blast',
          createdBy: currentUser?.uid,
          createdByEmail: currentUser?.email,
          salonId: salonId,
          timestamp: new Date().toISOString(),
          targetingType: formData.targetingType,
          targetingCriteria: buildTargetingCriteria(),
          scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
          scheduledTime: formData.scheduledTime,
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          isPersonalizationDemo: hasPersonalization
        }
      };

      await addDoc(collection(db, 'mail'), campaignData);

      const personalizationNote = hasPersonalization ? ' (with sample personalization)' : '';
      setSuccess(`Test email blast sent to ${adminUsers.length} admin(s)${personalizationNote}`);
      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      console.error('Error sending test email blast:', err);
      setError('Failed to send test email blast: ' + err.message);
    } finally {
      setSaving('');
    }
  };

  const handleSendActualEmailBlast = async () => {
    if (!validateEmailCampaign()) {
      showValidationErrors(emailErrors);
      return;
    }

    try {
      setSaving('actual');
      setError('');

      let targetedCustomers = await getTargetedCustomers();

      if (targetedCustomers.length === 0) {
        throw new Error('No customers match the targeting criteria or all matching customers have email notifications disabled');
      }

      // Apply frequency exclusion before sending
      const { eligible, excluded } = await applyFrequencyExclusionToList(salonId, 'email', targetedCustomers);
      targetedCustomers = eligible;
      if (targetedCustomers.length === 0) {
        throw new Error(`All ${excluded.length} targeted customer(s) have been excluded by frequency limits. Try again later or adjust your frequency settings.`);
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      // If scheduled time is within 2 minutes, send immediately via Firebase Extension.
      // Otherwise, write to emailCampaigns so the backend scheduler fires it at the right time.
      const isImmediate = (scheduledFor.getTime() - Date.now()) <= 2 * 60 * 1000;
      const targetCollection = isImmediate ? 'mail' : 'emailCampaigns';

      // Always send one email per recipient so each gets a customerId in metadata,
      // which lets the backend generate a per-recipient HMAC unsubscribe URL.
      const hasPersonalization = formData.subject.includes('{') || formData.body.includes('{');

      // Generate a shared batch ID and timestamp so all docs from this campaign blast can be grouped
      const campaignBatchId = `campaign_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const campaignTimestamp = new Date().toISOString();

      const mailPromises = targetedCustomers.map(async (customer) => {
        let personalizedSubject = formData.subject;
        let personalizedBody = formData.body;

        if (hasPersonalization) {
          // Get customer's latest appointment and transaction for personalization
          const [appointment, transaction] = await Promise.all([
            getCustomerAppointment(customer.id, salonId),
            getCustomerTransaction(customer.id, salonId)
          ]);

          [personalizedSubject, personalizedBody] = await Promise.all([
            personalizeMessage(formData.subject, customer, {
            promotional: formData.messageCategory === 'promotional',
              salonId, salonName, appointment, transaction,
              salonPhone, services: salonServices, staffList: salonStaff
            }),
            personalizeMessage(formData.body, customer, {
            promotional: formData.messageCategory === 'promotional',
              salonId, salonName, appointment, transaction,
              salonPhone, services: salonServices, staffList: salonStaff
            })
          ]);
        }

        const campaignData = {
          type: 'email',
          salonId: salonId,
          subject: personalizedSubject,
          html: getEmailHtml(personalizedBody, formData.messageCategory),
          to: [customer.email],
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          ...(!isImmediate && {
            status: 'scheduled',
            scheduledFor,
            createdAt: new Date()
          }),
          metadata: {
            campaignType: 'actual_blast',
            campaignBatchId: campaignBatchId,
            createdBy: currentUser?.uid,
            createdByEmail: currentUser?.email,
            salonId: salonId,
            timestamp: campaignTimestamp,
            targetingType: formData.targetingType,
            targetingCriteria: buildTargetingCriteria(),
            scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
            scheduledTime: formData.scheduledTime,
            schedulingType: formData.schedulingType,
            recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
            originalSubject: formData.subject,
            customerId: customer.id,
            customerName: customer.displayName,
            conversionPeriod: Number(formData.conversionPeriod) || 30
          }
        };

        return addDoc(collection(db, targetCollection), campaignData);
      });

      await Promise.all(mailPromises);

      // Record deliveries for frequency tracking
      await recordCampaignDeliveries(salonId, 'email', targetedCustomers);

      let successMessage;
      if (isImmediate) {
        successMessage = `Email campaign sent to ${targetedCustomers.length} customer(s)`;
      } else if (formData.schedulingType === 'one-time') {
        successMessage = `Email campaign scheduled for ${formData.scheduledDate} at ${formData.scheduledTime} — will automatically send to ${targetedCustomers.length} customer(s)`;
      } else {
        const endInfo = formData.recurring.endType === 'date'
          ? `until ${formData.recurring.endDate}`
          : `for ${formData.recurring.numberOfCycles} cycles`;
        successMessage = `Recurring email campaign scheduled (${formData.recurring.frequency}) starting ${formData.recurring.startDate} at ${formData.scheduledTime} ${endInfo} — will automatically send to ${targetedCustomers.length} customer(s)`;
      }

      if (excluded.length > 0) {
        successMessage += ` (${excluded.length} customer(s) excluded by frequency limits)`;
      }

      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 5000);

      resetEmailForm();

    } catch (err) {
      console.error('Error scheduling email blast:', err);
      setError('Failed to schedule email blast: ' + err.message);
    } finally {
      setSaving('');
    }
  };

  const resetEmailForm = () => {
    setFormData({
      subject: '',
      body: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: 'not_selected',
      targetingCriteria: {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 0,
        lastTransactionUnit: 'months',
        transactionCount: 0,
        transactionType: 'not_selected',
        transactionAmountType: 'not_selected',
        transactionAmount: 0,
        transactionLocationId: 'not_selected',
        memberTiers: [],
        pointsType: 'not_selected',
        pointsAmount: 0,
        specificCustomers: [],
        appointmentTimeValue: 0,
        appointmentTimeUnit: 'days',
        appointmentService: 'not_selected',
        appointmentStaff: 'not_selected',
        appointmentStatus: []
      }
    });
    setEmailErrors({});
    setFieldErrors({});
    setSelectedCustomers([]);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setTargetingCriteriaSaved(false);
    setIsHtmlTemplate(false);
    setShowHtmlPreview(false);
  };

  const handleInputChange = (field, value) => {
    // NEW: Reset targeting criteria saved state and clear criteria when targeting type changes
    if (field === 'targetingType') {
      setTargetingCriteriaSaved(false);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          transactionLocationId: 'not_selected',
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          appointmentTimeValue: 0,
          appointmentTimeUnit: 'days',
          appointmentService: 'not_selected',
          appointmentStaff: 'not_selected',
          appointmentStatus: []
        }
      }));
      setSelectedCustomers([]);

      if (emailErrors[field]) {
        setEmailErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      if (fieldErrors[field]) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate mandatory fields on change
    if (['subject', 'body', 'scheduledDate', 'scheduledTime'].includes(field)) {
      validateField(field, value);
    }

    if (emailErrors[field]) {
      setEmailErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  useEffect(() => {
    const pendingData = sessionStorage.getItem('pendingNotificationData');
    if (pendingData) {
      try {
        const notificationData = JSON.parse(pendingData);
        
        if (notificationData.type === 'email') {
          setFormData({
            ...formData,
            subject: notificationData.subject,
            body: notificationData.body,
            targetingType: notificationData.targetingType,
            targetingCriteria: notificationData.targetingCriteria
          });
          
          sessionStorage.removeItem('pendingNotificationData');
        }
      } catch (error) {
        console.error('Error parsing pending notification data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleTargetingCriteriaChange = (field, value) => {
    setFormData({
      ...formData,
      targetingCriteria: {
        ...formData.targetingCriteria,
        [field]: value
      }
    });
    // NEW: Mark as unsaved when criteria changes
    setTargetingCriteriaSaved(false);
  };
  
  const handlePostalCodesChange = (e) => {
    const codes = e.target.value.split(',').map(code => code.trim()).filter(code => code);
    handleTargetingCriteriaChange('postalCodes', codes);
  };

  const handleBirthMonthsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('birthMonths', selectedOptions);
  };

  const handleMemberTiersChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('memberTiers', selectedOptions);
  };

  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('emailSubject');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.subject.substring(0, start) + emoji + formData.subject.substring(end);
      handleInputChange('subject', newValue);
      
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const insertEmojiInBody = (emoji) => {
    const textarea = document.getElementById('emailBody');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.body.substring(0, start) + emoji + formData.body.substring(end);
      handleInputChange('body', newValue);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const handleRecurringChange = (field, value) => {
    setFormData({
      ...formData,
      recurring: {
        ...formData.recurring,
        [field]: value
      }
    });
  };

  const insertImage = () => {
    if (!imageUrl.trim()) {
      alert('Please enter an image URL');
      return;
    }

    const textarea = document.getElementById('emailBody');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const imageTag = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto; display: block; margin: 0 auto;">`;
      // Wrap with blank lines so it becomes its own block when converted to HTML
      const before = formData.body.substring(0, start);
      const after = formData.body.substring(end);
      const needsLeadingBreak = before.length > 0 && !before.endsWith('\n\n');
      const needsTrailingBreak = after.length > 0 && !after.startsWith('\n\n');
      const insertion = `${needsLeadingBreak ? '\n\n' : ''}${imageTag}${needsTrailingBreak ? '\n\n' : ''}`;
      const newValue = before + insertion + after;
      handleInputChange('body', newValue);

      setImageUrl('');
      setShowImageInput(false);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + insertion.length;
        textarea.focus();
      }, 0);
    }
  };
  
  return (
    <div className="notifications-editor">
      <h2>Email Campaigns</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Sub-tab Navigation */}
      <div className="sub-tab-navigation">
        <button
          className={`sub-tab-button ${activeSubTab === 'campaign-frequency' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('campaign-frequency')}
        >
          Campaign Frequency
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'create-campaign' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('create-campaign')}
        >
          Create Campaign
        </button>
      </div>

      {previewLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">Loading customer preview...</div>
          </div>
        </div>
      )}
      
      <CustomerPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        previewData={previewData}
        campaignName={formData.subject || 'email campaign'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete Template</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this template?</p>
              <p className="delete-warning">
                <strong>{selectedTemplate?.title}</strong>
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={confirmDeleteTemplate}
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Frequency Tab Content */}
      {activeSubTab === 'campaign-frequency' && (
        <CampaignFrequencyControl salonId={salonId} campaignType="email" />
      )}

      {/* Create Campaign Tab Content */}
      {activeSubTab === 'create-campaign' && (
        <>
          {/* Email Campaign Templates Section */}
      <div className="templates-section">
        <h3>Email Campaign Templates</h3>
        <p className="templates-description">Choose a pre-built template to get started quickly. Click on a template and then "Create Campaign" to populate the form.</p>

        <div className="template-category-tabs">
          <button
            className={`template-tab ${selectedTemplateCategory === 'transactional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('transactional')}
          >
            Transactional Campaigns
          </button>
          <button
            className={`template-tab ${selectedTemplateCategory === 'promotional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('promotional')}
          >
            Promotional Campaigns
          </button>
        </div>

        <div className="templates-grid">
          {getAllTemplates(selectedTemplateCategory).map((template) => (
            <div
              key={template.id}
              className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''} ${template.isCustom ? 'custom-template' : ''}`}
              onClick={() => setSelectedTemplate(template)}
            >
              <div className="template-card-header">
                <div className="template-title">
                  {template.title}
                  {template.isCustom && <span className="custom-badge">Custom</span>}
                </div>
                {template.messageType !== 'not_selected' && (
                  <div className="template-badge">{template.messageType.replace('_', ' ')}</div>
                )}
              </div>
              <div className="template-preview">{template.body.substring(0, 100)}...</div>
              {template.isSystemTemplate && (
                <div className="system-template-note" style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginTop: '4px' }}>
                  {template.systemNote}
                </div>
              )}
              {template.id === 'welcome_email' && (
                <div style={{ fontSize: '11px', color: '#b07a50', marginTop: '6px', padding: '5px 8px', background: '#fdf6f0', borderRadius: '4px', border: '1px solid #f0dece' }}>
                  <strong>Auto-appended:</strong> Hero image · About Us · Find Us · Follow Us (from Salon Settings)
                </div>
              )}
              {selectedTemplate?.id === template.id && (
                <div className="template-actions">
                  {!template.isSystemTemplate && !template.isAutoTriggered && (
                    <button
                      className="template-load-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLoadTemplate(template);
                      }}
                    >
                      Create Campaign
                    </button>
                  )}
                  {(template.isAutoTriggered || template.isSystemTemplate) && template.messageCategory === 'transactional' && template.id !== 'magic_link_signin' && (
                    <button
                      className={campaignSettings[`email_${template.id}`] !== false ? 'template-delete-button' : 'template-load-button'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCampaign(template);
                      }}
                    >
                      {campaignSettings[`email_${template.id}`] !== false ? 'Disable Campaign' : 'Enable Campaign'}
                    </button>
                  )}
                  <button
                    className="template-revise-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReviseTemplate(template);
                    }}
                  >
                    Revise Template
                  </button>
                  {template.isCustom && !template.isSystemTemplate && (
                    <button
                      className="template-delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate();
                      }}
                    >
                      Delete Template
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="notification-form">
        <h3>Create Email Campaign</h3>
        
        <div className="form-section">
          <h4>Subject</h4>
          
          <div className="form-group">
            <label htmlFor="emailSubject">
              Subject Line<span className="required-asterisk">*</span>
            </label>
            <div className="input-with-emojis">
              <input
                type="text"
                id="emailSubject"
                name="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                onBlur={handleFieldBlur}
                className={fieldErrors.subject ? 'input-error' : ''}
                maxLength={100}
                required
              />
              {fieldErrors.subject && <span className="field-error-message">{fieldErrors.subject}</span>}
              <div className="emoji-picker">
                <span>Add emoji:</span>
                {commonEmojis.map(emoji => (
                  <button 
                    key={emoji} 
                    type="button" 
                    className="emoji-button"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-helper-text">
              Max 100 characters
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h4>Message Content</h4>

          {/* HTML Template Mode banner */}
          {isHtmlTemplate && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#f0f7ff', border: '1px solid #d0e4f7', borderRadius: 6,
              padding: '10px 14px', marginBottom: 12
            }}>
              <span style={{ fontSize: 13, color: '#1a5c9e', fontWeight: 500 }}>
                📧 AI-generated HTML email template loaded
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowHtmlPreview(v => !v)}
                  style={{
                    padding: '5px 12px', borderRadius: 5, fontSize: 12,
                    border: '1px solid #1a5c9e', background: '#fff',
                    color: '#1a5c9e', cursor: 'pointer'
                  }}
                >
                  {showHtmlPreview ? 'Hide Preview' : 'Preview'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsHtmlTemplate(false);
                    setShowHtmlPreview(false);
                    setFormData(prev => ({ ...prev, body: '' }));
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: 5, fontSize: 12,
                    border: '1px solid #c0392b', background: '#fff',
                    color: '#c0392b', cursor: 'pointer'
                  }}
                >
                  Clear Template
                </button>
              </div>
            </div>
          )}

          {/* HTML Preview iframe */}
          {isHtmlTemplate && showHtmlPreview && formData.body && (
            <div style={{ marginBottom: 12, border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
              <iframe
                title="HTML Email Preview"
                sandbox="allow-same-origin"
                srcDoc={formData.body}
                style={{ width: '100%', minHeight: 500, border: 'none', display: 'block' }}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="emailBody">
              {isHtmlTemplate ? 'HTML Template (read-only — clear to switch to text mode)' : 'Message Body'}
              <span className="required-asterisk">*</span>
            </label>
            <div className="textarea-with-emojis">
              <textarea
                id="emailBody"
                name="body"
                value={formData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                onBlur={handleFieldBlur}
                rows={isHtmlTemplate ? 6 : 10}
                placeholder="Write your email message here...

You can include images using the button below.
Press Enter to create new lines and paragraphs."
                className={fieldErrors.body ? 'input-error' : ''}
                readOnly={isHtmlTemplate}
                style={isHtmlTemplate ? { background: '#f8f8f8', color: '#666', fontSize: 12 } : {}}
                required
              />
              {fieldErrors.body && <span className="field-error-message">{fieldErrors.body}</span>}
              {!isHtmlTemplate && (
              <div className="emoji-picker">
                <span>Add emoji:</span>
                {commonEmojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-button"
                    onClick={() => insertEmojiInBody(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
                <span style={{ marginLeft: '20px' }}>Insert image:</span>
                <button
                  type="button"
                  className="emoji-button"
                  onClick={() => setShowImageInput(!showImageInput)}
                  style={{ padding: '4px 12px' }}
                >
                  🖼️ Image
                </button>
              </div>
              )}
              
              {templateToEdit?.id === 'welcome_email' && (
                <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fdf6f0', border: '1px solid #f0dece', borderRadius: '6px', fontSize: '12px', color: '#7a5230', lineHeight: '1.6' }}>
                  <strong style={{ display: 'block', marginBottom: '4px', color: '#a0622a' }}>Automatically appended below your message body (not editable here)</strong>
                  The sections below your message body (hero image, Our Services, Opening Hours, About Us, Find Us, Follow Us) are automatically appended from your <strong>Salon Settings</strong> — edit them there.
                </div>
              )}

              {!isHtmlTemplate && showImageInput && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Image URL (or GIF URL):</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <button 
                      type="button"
                      onClick={insertImage}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Insert
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        setShowImageInput(false);
                        setImageUrl('');
                      }}
                      style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#6c757d', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Tip: You can use image hosting services like Imgur, Cloudinary, or your own server. GIFs are also supported!
                  </div>
                </div>
              )}
            </div>
            <div className="form-helper-text">
              Line breaks and paragraphs will be preserved in the email. An unsubscribe link will be automatically added with proper spacing.
            </div>
          </div>
        </div>

        {(formData.subject || formData.body) && (
          <div className="form-section">
            <h4>Message Preview</h4>
            {/* Device selector pills */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              <button type="button" onClick={() => setPreviewDevice('mobile')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'mobile' ? '#c47a6a' : '#ddd'}`, background: previewDevice === 'mobile' ? '#fdf0ee' : '#fafafa', color: previewDevice === 'mobile' ? '#c47a6a' : '#888', fontWeight: previewDevice === 'mobile' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="9" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/><rect x="3.5" y="11" width="3" height="1" rx="0.5" fill="currentColor"/></svg>
                Mobile
              </button>
              <button type="button" onClick={() => setPreviewDevice('laptop')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'laptop' ? '#c47a6a' : '#ddd'}`, background: previewDevice === 'laptop' ? '#fdf0ee' : '#fafafa', color: previewDevice === 'laptop' ? '#c47a6a' : '#888', fontWeight: previewDevice === 'laptop' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="13" height="8" rx="1" stroke="currentColor" strokeWidth="1"/><rect x="2" y="2" width="10" height="5.5" rx="0.3" fill="currentColor" opacity="0.18"/><rect x="0" y="9" width="14" height="1.5" rx="0.75" fill="currentColor" opacity="0.5"/></svg>
                Laptop
              </button>
            </div>
            {/* Device frame */}
            <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
              {previewDevice === 'mobile' ? (
                /* Realistic iPhone frame — 86px wide, 495px screen height */
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  {/* Mute switch */}
                  <div style={{ position: 'absolute', left: '-3px', top: '58px', width: '3px', height: '11px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Volume up */}
                  <div style={{ position: 'absolute', left: '-3px', top: '76px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Volume down */}
                  <div style={{ position: 'absolute', left: '-3px', top: '102px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* Power button */}
                  <div style={{ position: 'absolute', right: '-3px', top: '82px', width: '3px', height: '28px', background: '#3a3a3a', borderRadius: '0 2px 2px 0', boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.1)' }} />
                  {/* iPhone body */}
                  <div style={{ width: '224px', background: 'linear-gradient(170deg,#2d2d2d 0%,#1a1a1a 50%,#232323 100%)', borderRadius: '22px', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 22px 55px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                    {/* Top bezel + Dynamic Island */}
                    <div style={{ background: '#000', paddingTop: '10px', paddingBottom: '5px', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '50px', height: '14px', background: '#0d0d0d', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '5px', boxShadow: '0 0 0 0.5px #222' }}>
                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#151515', boxShadow: '0 0 0 0.5px #2a2a2a, inset 0 0 2px rgba(0,100,200,0.25)' }} />
                      </div>
                    </div>
                    {/* Screen — 495px, vertical scroll only */}
                    <div style={{ height: '362px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', background: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                      {/* Sticky mail header */}
                      <div style={{ background: '#f2f2f7', padding: '8px 8px 6px', borderBottom: '1px solid #d1d1d6', position: 'sticky', top: 0, zIndex: 1, flexShrink: 0 }}>
                        <div style={{ fontSize: '8px', color: '#007aff', marginBottom: '3px' }}>← Inbox</div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#000', lineHeight: 1.25, wordBreak: 'break-word' }}>
                          {formData.subject || <span style={{ fontStyle: 'italic', fontWeight: '400', color: '#bbb' }}>No subject</span>}
                        </div>
                        <div style={{ fontSize: '7.5px', color: '#888', marginTop: '3px' }}>{salonName || 'Bespoke Beauty'} · now</div>
                      </div>
                      {/* Email body */}
                      <div style={{ padding: '8px 8px' }}>
                        {formData.body ? (
                          (isHtmlTemplate || /<[a-z]/i.test(formData.body)) ? (
                            <div style={{ fontSize: '8px', color: '#333', lineHeight: '1.5', overflowX: 'hidden', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: '<style>img{max-width:100%!important;width:auto!important;height:auto!important;display:block!important;margin:4px 0!important;border-radius:3px;}</style>' + formData.body }} />
                          ) : (
                            <div style={{ fontSize: '8.5px', color: '#333', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {formData.body}
                            </div>
                          )
                        ) : (
                          <div style={{ fontSize: '8.5px', color: '#bbb', fontStyle: 'italic' }}>Email body will appear here…</div>
                        )}
                      </div>
                      {/* Auto-appended sections — welcome email only */}
                      {templateToEdit?.id === 'welcome_email' && salonConfig && (() => {
                        const pc = salonConfig.branding?.colors?.primary || '#c47a6a';
                        const heroUrl = salonConfig.branding?.images?.hero;
                        const aboutUs = salonConfig.aboutUs;
                        const addr = salonConfig.address;
                        const emailAddr = salonConfig.email;
                        const phone = salonConfig.phone;
                        const social = salonConfig.socialMedia || {};
                        const hours = salonConfig.businessHours?.regularHours || [];
                        const activeServices = salonServices.filter(s => s.isActive !== false).slice(0, 8);
                        return (
                          <div style={{ margin: '0 8px 8px', fontSize: '7.5px', color: '#333', lineHeight: '1.5' }}>
                            {/* Hero image */}
                            {heroUrl && (
                              <div style={{ marginBottom: '6px' }}>
                                <img src={heroUrl} alt="Salon" style={{ width: '100%', height: '70px', objectFit: 'cover', borderRadius: '4px', display: 'block' }} />
                              </div>
                            )}
                            {/* Services — rendered after hero */}
                            {activeServices.length > 0 && (
                              <div style={{ marginBottom: '6px' }}>
                                <div style={{ fontWeight: '700', color: pc, marginBottom: '3px' }}>Our Services</div>
                                {activeServices.map((s, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span>{s.name}{s.description && <span style={{ color: '#888', display: 'block', fontSize: '7px' }}>{s.description}</span>}</span>
                                    <span style={{ color: '#888', flexShrink: 0, marginLeft: '4px' }}>{s.duration ? s.duration + ' min' : ''}</span>
                                  </div>
                                ))}
                                {salonServices.filter(s => s.isActive !== false).length > 8 && (
                                  <div style={{ color: '#888', marginTop: '2px' }}>And {salonServices.filter(s => s.isActive !== false).length - 8} more…</div>
                                )}
                              </div>
                            )}
                            {/* Business hours */}
                            {hours.length > 0 && (
                              <div style={{ marginBottom: '6px', padding: '5px 6px', background: '#f9f9f9', borderRadius: '3px' }}>
                                <div style={{ fontWeight: '700', color: pc, marginBottom: '2px' }}>Opening Hours</div>
                                {hours.map((h, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{h.day}</span>
                                    <span style={{ color: h.isClosed ? '#e05c5c' : '#555' }}>{h.isClosed ? 'Closed' : `${h.open} – ${h.close}`}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* About Us */}
                            {aboutUs && (
                              <div style={{ marginBottom: '6px', padding: '5px 6px', background: '#f9f9f9', borderRadius: '3px' }}>
                                <div style={{ fontWeight: '700', color: pc, marginBottom: '2px' }}>About Us</div>
                                <div style={{ color: '#555' }}>{aboutUs}</div>
                              </div>
                            )}
                            {/* Find Us */}
                            {(addr || emailAddr || phone) && (
                              <div style={{ marginBottom: '6px' }}>
                                <div style={{ fontWeight: '700', color: pc, marginBottom: '2px' }}>Find Us</div>
                                {addr && <div>{addr}</div>}
                                {emailAddr && <div style={{ color: pc }}>{emailAddr}</div>}
                                {phone && <div>{phone}</div>}
                              </div>
                            )}
                            {/* Follow Us */}
                            {(social.facebook || social.instagram || social.twitter) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '700', color: pc, marginBottom: '2px' }}>Follow Us</div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                  {social.facebook && <span style={{ color: pc }}>Facebook</span>}
                                  {social.instagram && <span style={{ color: pc }}>Instagram</span>}
                                  {social.twitter && <span style={{ color: pc }}>Twitter</span>}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Bottom bezel + home indicator */}
                    <div style={{ background: '#000', paddingTop: '6px', paddingBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.22)', borderRadius: '3px' }} />
                    </div>
                  </div>
                </div>
              ) : (
                /* Laptop frame — 2× width (560px) and 2× screen height (316px), scrollable */
                <div style={{ display: 'inline-block', flexShrink: 0 }}>
                  <div style={{ width: '483px', background: '#1c1c1e', borderRadius: '8px 8px 0 0', padding: '8px 7px 0', boxSizing: 'border-box', boxShadow: 'inset 0 0 0 1px #3a3a3a' }}>
                    <div style={{ width: '4px', height: '4px', background: '#444', borderRadius: '50%', margin: '0 auto 4px' }} />
                    <div style={{ background: '#fff', borderRadius: '3px 3px 0 0', height: '276px', overflowY: 'auto', scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                      <div style={{ background: '#f5f5f5', borderBottom: '1px solid #e0e0e0', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px', position: 'sticky', top: 0, zIndex: 1 }}>
                        <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#ff5f57', flexShrink: 0 }} />
                        <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#febc2e', flexShrink: 0 }} />
                        <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#28c840', flexShrink: 0 }} />
                        <div style={{ flex: 1, marginLeft: '8px', fontSize: '8.5px', color: '#666', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: '500' }}>
                          {formData.subject || <span style={{ color: '#bbb' }}>No subject</span>}
                        </div>
                      </div>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a1a', marginBottom: '5px', wordBreak: 'break-word' }}>
                          {formData.subject || <span style={{ fontStyle: 'italic', fontWeight: '400', color: '#bbb', fontSize: '12px' }}>Subject line will appear here…</span>}
                        </div>
                        <div style={{ fontSize: '8.5px', color: '#888' }}>{salonName || 'Bespoke Beauty'} · now</div>
                      </div>
                      <div style={{ flex: 1, padding: '12px 16px' }}>
                        {formData.body ? (
                          (isHtmlTemplate || /<[a-z]/i.test(formData.body)) ? (
                            <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6', overflowX: 'hidden', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: '<style>img{max-width:100%!important;width:auto!important;height:auto!important;display:block!important;margin:6px 0!important;border-radius:4px;}</style>' + formData.body }} />
                          ) : (
                            <div style={{ fontSize: '11px', color: '#333', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {formData.body}
                            </div>
                          )
                        ) : (
                          <div style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>Email body will appear here…</div>
                        )}
                        {/* Auto-appended sections — welcome email only */}
                        {templateToEdit?.id === 'welcome_email' && salonConfig && (() => {
                          const pc = salonConfig.branding?.colors?.primary || '#c47a6a';
                          const heroUrl = salonConfig.branding?.images?.hero;
                          const aboutUs = salonConfig.aboutUs;
                          const addr = salonConfig.address;
                          const emailAddr = salonConfig.email;
                          const phone = salonConfig.phone;
                          const social = salonConfig.socialMedia || {};
                          const hours = salonConfig.businessHours?.regularHours || [];
                          const activeServices = salonServices.filter(s => s.isActive !== false).slice(0, 8);
                          return (
                            <div style={{ marginTop: '14px', fontSize: '10px', color: '#333', lineHeight: '1.6' }}>
                              {/* Hero image */}
                              {heroUrl && (
                                <div style={{ marginBottom: '10px' }}>
                                  <img src={heroUrl} alt="Salon" style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '6px', display: 'block' }} />
                                </div>
                              )}
                              {/* Services — rendered after hero */}
                              {activeServices.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                  <div style={{ fontWeight: '700', color: pc, marginBottom: '4px', fontSize: '11px' }}>Our Services</div>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                                    <thead>
                                      <tr style={{ background: '#f9f9f9' }}>
                                        <th style={{ padding: '4px 8px', textAlign: 'left', color: '#666', fontWeight: '600' }}>Service</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'center', color: '#666', fontWeight: '600' }}>Duration</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activeServices.map((s, i) => (
                                        <tr key={i}>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                            <strong>{s.name}</strong>
                                            {s.description && <div style={{ color: '#888', fontSize: '9px' }}>{s.description}</div>}
                                          </td>
                                          <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', color: '#666' }}>{s.duration ? s.duration + ' min' : ''}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {salonServices.filter(s => s.isActive !== false).length > 8 && (
                                    <div style={{ color: '#888', fontSize: '9px', marginTop: '4px' }}>And {salonServices.filter(s => s.isActive !== false).length - 8} more services available!</div>
                                  )}
                                </div>
                              )}
                              {/* Business hours */}
                              {hours.length > 0 && (
                                <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#f9f9f9', borderRadius: '5px' }}>
                                  <div style={{ fontWeight: '700', color: pc, marginBottom: '6px', fontSize: '11px' }}>Opening Hours</div>
                                  {hours.map((h, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '1px 0' }}>
                                      <span>{h.day}</span>
                                      <span style={{ color: h.isClosed ? '#e05c5c' : '#555' }}>{h.isClosed ? 'Closed' : `${h.open} – ${h.close}`}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* About Us */}
                              {aboutUs && (
                                <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#f9f9f9', borderRadius: '5px' }}>
                                  <div style={{ fontWeight: '700', color: pc, marginBottom: '4px', fontSize: '11px' }}>About Us</div>
                                  <div style={{ color: '#555', fontSize: '10px' }}>{aboutUs}</div>
                                </div>
                              )}
                              {/* Find Us */}
                              {(addr || emailAddr || phone) && (
                                <div style={{ marginBottom: '12px' }}>
                                  <div style={{ fontWeight: '700', color: pc, marginBottom: '4px', fontSize: '11px' }}>Find Us</div>
                                  {addr && <div>{addr}</div>}
                                  {emailAddr && <div style={{ color: pc }}>{emailAddr}</div>}
                                  {phone && <div>{phone}</div>}
                                </div>
                              )}
                              {/* Follow Us */}
                              {(social.facebook || social.instagram || social.twitter) && (
                                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                                  <div style={{ fontWeight: '700', color: pc, marginBottom: '4px', fontSize: '11px' }}>Follow Us</div>
                                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                    {social.facebook && <span style={{ color: pc }}>Facebook</span>}
                                    {social.instagram && <span style={{ color: pc }}>Instagram</span>}
                                    {social.twitter && <span style={{ color: pc }}>Twitter</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div style={{ width: '483px', height: '10px', background: '#2c2c2e' }} />
                  <div style={{ width: '515px', height: '8px', background: '#1c1c1e', borderRadius: '0 0 6px 6px', marginLeft: '-16px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
                </div>
              )}
            </div>
            <div className="form-helper-text" style={{ marginTop: '10px', textAlign: 'center' }}>
              Preview of how the email will appear on a recipient's {previewDevice === 'mobile' ? 'phone' : 'desktop'}.{templateToEdit?.id === 'welcome_email' ? ' Sections below the message body are pulled live from your Salon Settings.' : ' An unsubscribe link will be appended automatically.'}
            </div>
          </div>
        )}

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Scheduling</h4>

          <div className="form-group">
            <label>Campaign Type *</label>
            <div className="scheduling-type-tabs">
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'one-time' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'one-time')}
                disabled={templateEditMode}
              >
                One-Time Campaign
              </button>
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'recurring' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'recurring')}
                disabled={templateEditMode}
              >
                Recurring Campaign
              </button>
            </div>
          </div>

          {formData.schedulingType === 'one-time' ? (
            <div className="one-time-scheduling">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="scheduledDate">
                    Scheduled Date<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    id="scheduledDate"
                    name="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    onBlur={handleFieldBlur}
                    min={new Date().toISOString().split('T')[0]}
                    className={fieldErrors.scheduledDate ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledDate && <span className="field-error-message">{fieldErrors.scheduledDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="scheduledTime">
                    Scheduled Time<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="time"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                    onBlur={handleFieldBlur}
                    className={fieldErrors.scheduledTime ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledTime && <span className="field-error-message">{fieldErrors.scheduledTime}</span>}
                </div>
              </div>

              {emailErrors.scheduledDateTime && (
                <div className="error-message">
                  {emailErrors.scheduledDateTime}
                </div>
              )}
            </div>
          ) : (
            <div className="recurring-scheduling">
              <div className="form-group">
                <label htmlFor="recurringFrequency">
                  Frequency *
                </label>
                <select
                  id="recurringFrequency"
                  value={formData.recurring.frequency}
                  onChange={(e) => handleRecurringChange('frequency', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>

              {formData.recurring.frequency === 'weekly' && (
                <div className="form-group">
                  <label htmlFor="dayOfWeek">Day of Week *</label>
                  <select
                    id="dayOfWeek"
                    value={formData.recurring.dayOfWeek}
                    onChange={(e) => handleRecurringChange('dayOfWeek', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {formData.recurring.frequency === 'monthly' && (
                <div className="form-group">
                  <label htmlFor="dayOfMonth">Day of Month *</label>
                  <input
                    type="number"
                    id="dayOfMonth"
                    min="1"
                    max="31"
                    value={formData.recurring.dayOfMonth}
                    onChange={(e) => handleRecurringChange('dayOfMonth', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">Enter a number between 1-31</div>
                </div>
              )}

              {formData.recurring.frequency === 'annually' && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="monthOfYear">Month *</label>
                    <select
                      id="monthOfYear"
                      value={formData.recurring.monthOfYear}
                      onChange={(e) => handleRecurringChange('monthOfYear', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="january">January</option>
                      <option value="february">February</option>
                      <option value="march">March</option>
                      <option value="april">April</option>
                      <option value="may">May</option>
                      <option value="june">June</option>
                      <option value="july">July</option>
                      <option value="august">August</option>
                      <option value="september">September</option>
                      <option value="october">October</option>
                      <option value="november">November</option>
                      <option value="december">December</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="dayOfYear">Day *</label>
                    <input
                      type="number"
                      id="dayOfYear"
                      min="1"
                      max="31"
                      value={formData.recurring.dayOfYear}
                      onChange={(e) => handleRecurringChange('dayOfYear', parseInt(e.target.value))}
                      disabled={templateEditMode}
                    />
                    <div className="form-helper-text">Day of the month (1-31)</div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="recurringTime">Time *</label>
                <input
                  type="time"
                  id="recurringTime"
                  value={formData.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Time when the campaign should run</div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recurringStartDate">Start Date *</label>
                  <input
                    type="date"
                    id="recurringStartDate"
                    value={formData.recurring.startDate}
                    onChange={(e) => handleRecurringChange('startDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Campaign End *</label>
                <div className="recurring-end-type">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="date"
                      checked={formData.recurring.endType === 'date'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End on specific date</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="cycles"
                      checked={formData.recurring.endType === 'cycles'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End after number of cycles</span>
                  </label>
                </div>
              </div>

              {formData.recurring.endType === 'date' ? (
                <div className="form-group">
                  <label htmlFor="recurringEndDate">End Date *</label>
                  <input
                    type="date"
                    id="recurringEndDate"
                    value={formData.recurring.endDate}
                    onChange={(e) => handleRecurringChange('endDate', e.target.value)}
                    min={formData.recurring.startDate || new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="numberOfCycles">Number of Cycles *</label>
                  <input
                    type="number"
                    id="numberOfCycles"
                    min="1"
                    value={formData.recurring.numberOfCycles}
                    onChange={(e) => handleRecurringChange('numberOfCycles', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">How many times should this campaign run?</div>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '25px', paddingTop: '25px', borderTop: '2px solid #e0e0e0' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.recurring.excludePreviousRecipients}
                    onChange={(e) => handleRecurringChange('excludePreviousRecipients', e.target.checked)}
                    disabled={templateEditMode}
                  />
                  <span>Exclude previous recipients from each campaign run</span>
                </label>
                <div className="form-helper-text">
                  Prevent customers who received this campaign in the past from receiving it again
                </div>
              </div>

              {formData.recurring.excludePreviousRecipients && (
                <div className="exclusion-settings">
                  <div className="form-group">
                    <label>Exclusion Period</label>
                    <div className="recurring-end-type">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="cycles"
                          checked={formData.recurring.exclusionType === 'cycles'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude from past X cycles</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="date"
                          checked={formData.recurring.exclusionType === 'date'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude who received before specific date</span>
                      </label>
                    </div>
                  </div>

                  {formData.recurring.exclusionType === 'cycles' ? (
                    <div className="form-group">
                      <label htmlFor="excludePastCycles">
                        Exclude past cycles
                        {emailErrors.excludePastCycles && <span className="error-text"> - {emailErrors.excludePastCycles}</span>}
                      </label>
                      <input
                        type="number"
                        id="excludePastCycles"
                        min="1"
                        value={formData.recurring.excludePastCycles}
                        onChange={(e) => handleRecurringChange('excludePastCycles', parseInt(e.target.value))}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received this in the last X campaign runs
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="excludeBeforeDate">
                        Exclude before date
                        {emailErrors.excludeBeforeDate && <span className="error-text"> - {emailErrors.excludeBeforeDate}</span>}
                      </label>
                      <input
                        type="date"
                        id="excludeBeforeDate"
                        value={formData.recurring.excludeBeforeDate}
                        onChange={(e) => handleRecurringChange('excludeBeforeDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received campaigns of this type before this date
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Conversion Tracking</h4>
          <div className="form-group">
            <label htmlFor="conversionPeriod">
              Conversion Period (days)
            </label>
            <input
              type="number"
              id="conversionPeriod"
              name="conversionPeriod"
              min="1"
              max="365"
              value={formData.conversionPeriod}
              onChange={(e) => handleInputChange('conversionPeriod', Math.max(1, parseInt(e.target.value, 10) || 30))}
              disabled={templateEditMode}
              style={{ width: 120 }}
            />
            <div className="form-helper-text">
              Bookings made within this many days of the campaign being sent will be attributed as conversions from this campaign.
            </div>
          </div>
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Customer Targeting</h4>

          <div className="form-group">
            <label htmlFor="targetingType">Target Audience<span className="required-asterisk">*</span></label>
            <select
              id="targetingType"
              name="targetingType"
              value={formData.targetingType}
              onChange={(e) => handleInputChange('targetingType', e.target.value)}
              onBlur={handleFieldBlur}
              className={fieldErrors.targetingType ? 'input-error' : ''}
              disabled={templateEditMode}
            >
              <option value="not_selected">Not Selected</option>
              <option value="all_customers">All Customers</option>
              <option value="customer_profile">Customer Profile Based</option>
              <option value="customer_transaction">Customer Transaction Based</option>
              <option value="customer_points">Customer Points Based</option>
              <option value="customer_appointment">Customer Appointment Based</option>
              <option value="specific_customer">Specific Customer</option>
              <option value="customer_notes">Customer Notes Based</option>
            </select>
            {fieldErrors.targetingType && <span className="field-error-message">{fieldErrors.targetingType}</span>}
          </div>
          
          {formData.targetingType === 'customer_profile' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Birth Months</label>
                <select
                  multiple
                  value={formData.targetingCriteria.birthMonths}
                  onChange={handleBirthMonthsChange}
                  size="6"
                  disabled={templateEditMode}
                >
                  {birthMonthOptions.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple months
                </div>
              </div>

              <div className="form-group">
                <label>Postal Codes (comma-separated)</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.postalCodes.join(', ')}
                  onChange={handlePostalCodesChange}
                  placeholder="e.g., 560, 123456, 567890"
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">
                  Leave empty for all postal codes. Use partial codes for broader targeting.
                </div>
              </div>
            </div>
          )}
          
          {formData.targetingType === 'customer_transaction' && (
            <div className="targeting-criteria">
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location Name</label>
                  <select
                    value={formData.targetingCriteria.transactionLocationId || 'not_selected'}
                    onChange={(e) => handleTargetingCriteriaChange('transactionLocationId', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="not_selected">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.locationName}{loc.isHeadOffice ? ' (Head Office)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="form-helper-text">
                    Filter by transactions at a specific location, or leave as "All Locations".
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Transaction Count Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="less_than">Less than X transactions</option>
                  <option value="equal_to">Exactly X transactions</option>
                  <option value="greater_than">More than X transactions</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Transactions</label>
                  <input
                    type="number"
                    value={formData.targetingCriteria.transactionCount}
                    onChange={(e) => handleTargetingCriteriaChange('transactionCount', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                  />
                </div>
                <div className="form-group">
                  <label>Last Transaction within</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={formData.targetingCriteria.lastTransactionMonths}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionMonths', Number(e.target.value))}
                      min="0"
                      disabled={templateEditMode}
                      style={{ width: '80px' }}
                    />
                    <select
                      value={formData.targetingCriteria.lastTransactionUnit || 'months'}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionUnit', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Spend Amount Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionAmountType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmountType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Amount</option>
                  <option value="less_than">Less Than the Following Amount</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.transactionAmount}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmount', Number(e.target.value))}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_points' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Member Tiers</label>
                <select
                  multiple
                  value={formData.targetingCriteria.memberTiers}
                  onChange={handleMemberTiersChange}
                  size="5"
                  disabled={templateEditMode}
                >
                  {memberTierOptions.map(tier => (
                    <option key={tier} value={tier}>{tier}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple tiers
                </div>
              </div>

              <div className="form-group">
                <label>Points Criteria</label>
                <select
                  value={formData.targetingCriteria.pointsType}
                  onChange={(e) => handleTargetingCriteriaChange('pointsType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Number of Points</option>
                  <option value="less_than">Less Than the Following Number of Points</option>
                </select>
              </div>

              <div className="form-group">
                <label>Number of Points</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.pointsAmount}
                  onChange={(e) => handleTargetingCriteriaChange('pointsAmount', Number(e.target.value))}
                  min="0"
                  placeholder="0"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_appointment' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Upcoming Request within</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.targetingCriteria.appointmentTimeValue}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeValue', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                    style={{ width: '80px' }}
                  />
                  <select
                    value={formData.targetingCriteria.appointmentTimeUnit}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeUnit', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Service Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentService}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentService', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Service)</option>
                  {salonServices.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading services...</span>}
              </div>

              <div className="form-group">
                <label>Staff Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentStaff}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentStaff', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Staff)</option>
                  {salonStaff.map(staff => (
                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading staff...</span>}
              </div>

              <div className="form-group">
                <label>Appointment Status</label>
                <select
                  multiple
                  value={formData.targetingCriteria.appointmentStatus}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    handleTargetingCriteriaChange('appointmentStatus', selectedOptions);
                  }}
                  size="4"
                  disabled={templateEditMode}
                >
                  {appointmentStatusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple statuses. Leave empty for any status.
                </div>
              </div>
            </div>
          )}

          {formData.targetingType === 'specific_customer' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>
                  Search and Select Customers
                  {emailErrors.specificCustomers && <span className="error-text"> - {emailErrors.specificCustomers}</span>}
                </label>
                <div className="customer-search-container">
                  <input
                    type="text"
                    value={customerSearchQuery}
                    onChange={handleCustomerSearchChange}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search by customer name or email..."
                    className="customer-search-input"
                    disabled={templateEditMode}
                  />
                  
                  {showCustomerDropdown && (
                    <div className="customer-search-dropdown">
                      {searchingCustomers && (
                        <div className="customer-search-loading">Searching...</div>
                      )}
                      
                      {!searchingCustomers && customerSearchResults.length === 0 && customerSearchQuery.length >= 2 && (
                        <div className="customer-search-no-results">No customers found</div>
                      )}
                      
                      {!searchingCustomers && customerSearchResults.length > 0 && (
                        <>
                          {customerSearchResults.map(customer => (
                            <div 
                              key={customer.id} 
                              className="customer-search-result"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <div className="customer-result-name">{customer.displayName}</div>
                              <div className="customer-result-email">{customer.email}</div>
                              {customer.phoneNumber && (
                                <div className="customer-result-phone">{customer.phoneNumber}</div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {customerSearchQuery.length < 2 && (
                        <div className="customer-search-hint">Type at least 2 characters to search</div>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedCustomers.length > 0 && (
                  <div className="selected-customers">
                    <h5>Selected Customers ({selectedCustomers.length})</h5>
                    <div className="selected-customers-list">
                      {selectedCustomers.map(customer => (
                        <div key={customer.id} className="selected-customer-item">
                          <div className="selected-customer-info">
                            <div className="selected-customer-name">{customer.displayName}</div>
                            <div className="selected-customer-email">{customer.email}</div>
                          </div>
                          <button
                            type="button"
                            className="remove-customer-button"
                            onClick={() => handleCustomerRemove(customer.id)}
                            disabled={templateEditMode}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* #Revised UI_v2 — customer notes targeting */}
          {formData.targetingType === 'customer_notes' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Search Terms</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.notesSearchText || ''}
                  onChange={(e) => handleTargetingCriteriaChange('notesSearchText', e.target.value)}
                  placeholder="Enter words or phrase to search in customer notes..."
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Customers whose notes match the search terms will be targeted</div>
              </div>
              <div className="form-group">
                <label>Match Type</label>
                <select
                  value={formData.targetingCriteria.notesMatchType || 'contains_all'}
                  onChange={(e) => handleTargetingCriteriaChange('notesMatchType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="exact">Exact Match — notes contain this exact phrase</option>
                  <option value="contains_any">Contains Any Words — notes contain at least one of the words</option>
                  <option value="contains_all">Contains All Words — notes contain all words (any order)</option>
                </select>
              </div>
            </div>
          )}

          <div className="targeting-buttons">
            <button
              type="button"
              className="save-criteria-button"
              onClick={handleSaveTargetingCriteria}
              disabled={formData.targetingType === 'not_selected' || templateEditMode}
            >
              Save Targeting Criteria
            </button>
            <button
              type="button"
              className="preview-button"
              onClick={handlePreviewTargeting}
              disabled={previewLoading || !targetingCriteriaSaved || templateEditMode}
              style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
            >
              {previewLoading ? 'Loading...' : 'Preview Target Customers'}
            </button>
            {!templateEditMode && (
              <button
                type="button"
                className="add-template-button"
                onClick={handleCreateTemplate}
                disabled={formData.targetingType === 'not_selected'}
              >
                Add as Template
              </button>
            )}
            {!templateEditMode && (
              <button
                type="button"
                className="download-social-csv-button"
                onClick={handleDownloadSocialCSV}
                disabled={!targetingCriteriaSaved}
                style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
              >
                Download Target for Social Platforms (Meta, Instagram, TikTok)
              </button>
            )}
          </div>
        </div>

        {/* Final Customers Targeted Section */}
        <FinalCustomersTargeted
          salonId={salonId}
          campaignType="email"
          formData={formData}
          targetingCriteriaSaved={targetingCriteriaSaved}
          disabled={templateEditMode}
        />


        {templateEditMode ? (
          <div className="template-edit-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleSaveRevisedTemplate}
              disabled={saving !== ''}
            >
              {saving === 'template' ? 'Saving...' : 'Make Revisions to Template'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setTemplateEditMode(false);
                setTemplateToEdit(null);
                resetEmailForm();
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="campaign-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={resetEmailForm}
            >
              Cancel
            </button>

            <button
              type="button"
              className="test-blast-button"
              onClick={handleSendTestEmailBlast}
              disabled={saving !== ''}
            >
              {saving === 'test' ? 'Sending...' : 'Send Test Blast'}
            </button>

            <button
              type="button"
              className="actual-blast-button"
              onClick={handleSendActualEmailBlast}
              disabled={saving !== ''}
            >
              {saving === 'actual' ? 'Scheduling...' : 'Send Actual Blast'}
            </button>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}

export default EmailCampaignsEditor;